import { BlockID } from './Blocks/BaseBlock';
import { CheckboxesBlockValue } from './Blocks/Checkboxes/CheckboxesBlock';
import type { TableData } from './Blocks/Table/BaseTable/types';

export type BlockValue =
  | string
  | boolean
  | CheckboxesBlockValue
  | TableData
  | null;

export type BlockRecord<T extends BlockValue = BlockValue> = {
  value: T | null;
  comment?: string | null;
  changed?: boolean;
};

export type BlockRecords = Record<BlockID, BlockRecord>;

/**
 * PIER-885: some legacy consensus records stored a CustomTable value double-wrapped
 * as `{ value: [...rows...] }` instead of the canonical bare rows array. That shape
 * crashes the DE2 export and is copied forward on every re-save. Unwrap it on read
 * so downstream table handling — and any re-saved payload — uses the correct shape.
 * Only touches the exact corruption — a single-key `{ value: rows }` wrapper whose
 * rows are a TableData array; checkboxes (`{ options, otherOption }`), wrappers with
 * extra keys and healthy values pass through untouched. This definition must stay in
 * lockstep with the backend's Extraction::Consensus::DoubleWrappedTables: the backend
 * rejects on save exactly what this repairs on read, so any mismatch where the
 * backend is stricter leaves the decider unable to save at all.
 * Local row-array check (mirrors isTableBlockValue) — kept here rather than
 * imported so BlocksData does not take a runtime dependency on
 * Blocks/Table/BaseTable/types, which imports BlockValue back from BlocksData
 * (a circular import that crashes the bundle at boot).
 */
const isBareTableRows = (value: unknown): value is TableData =>
  Array.isArray(value) &&
  (value.length === 0 ||
    (typeof value[0] === 'object' && value[0] !== null && 'rowID' in value[0]));

export const unwrapDoubleWrappedTableValue = (
  value: BlockValue | null | undefined
): BlockValue | null => {
  if (!!value && !Array.isArray(value) && typeof value === 'object') {
    const keys = Object.keys(value);
    const inner = (value as { value?: unknown }).value;
    if (keys.length === 1 && keys[0] === 'value' && isBareTableRows(inner)) {
      return inner;
    }
  }
  return value ?? null;
};

export const readBlockValue = (
  records: BlockRecords,
  blockID: BlockID
): BlockValue | null =>
  unwrapDoubleWrappedTableValue(records[blockID]?.value ?? null);

/**
 * PIER-885: normalize a whole `BlockRecords` map before it is submitted, so the
 * consensus write path can never persist a double-wrapped table value again.
 *
 * A comment-only decision has no `value` (undefined); leave those records untouched
 * so the missing value stays distinct from `null` (which `unwrapDoubleWrappedTableValue`
 * would coerce it to). `resolveDecidedValues` treats missing as a resolved comment-only
 * decision and `null` as absent, so coercing here would silently discard the comment.
 * Only records that actually have a value are normalized.
 */
export const normalizeBlockRecordsForWrite = (
  records: BlockRecords
): BlockRecords =>
  Object.keys(records).reduce((acc, id) => {
    const record = records[id];
    return {
      ...acc,
      [id]:
        record.value === undefined
          ? record
          : { ...record, value: unwrapDoubleWrappedTableValue(record.value) },
    };
  }, {} as BlockRecords);

export function readBlockComment(
  data: BlockRecords,
  id: BlockID
): string | null {
  if (id in data) {
    return data[id].comment || null;
  }
  return null;
}
